import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, Calendar } from "lucide-react";
import { useMentors } from "@/hooks/useMentors";
import { MentorTierBadge } from "@/components/MentorTierBadge";
import TruncatedCredits from "@/components/TruncatedCredits";
import ComplianceFooter from "@/components/ComplianceFooter";

const defaultAvatar = '/lovable-uploads/1c000ebd-7faf-4987-a62e-8c04a9a373a2.png';

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [performedQuery, setPerformedQuery] = useState(searchParams.get('q') || '');
  const { mentors, loading } = useMentors();
  const [filteredMentors, setFilteredMentors] = useState(mentors);

  useEffect(() => {
    const query = performedQuery;
    
    if (!query.trim()) {
      setFilteredMentors(mentors);
      return;
    }

    const queryLower = query.toLowerCase().trim();
    const filtered = mentors.filter((mentor) => {
      // Search in name
      const fullName = `${mentor.first_name} ${mentor.last_name}`.toLowerCase();
      if (fullName.includes(queryLower)) return true;

      // Search in expertise
      if (mentor.expertise?.some(expertise => 
        expertise.toLowerCase().includes(queryLower)
      )) return true;

      // Search in credits
      if (mentor.credits?.some(credit => 
        credit.toLowerCase().includes(queryLower)
      )) return true;

      // Search in specialty
      if (mentor.specialty?.toLowerCase().includes(queryLower)) return true;

      // Search in bio
      if (mentor.bio?.toLowerCase().includes(queryLower)) return true;

      return false;
    });

    setFilteredMentors(filtered);
  }, [performedQuery, mentors]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setPerformedQuery(searchQuery.trim());
      setSearchParams({ q: searchQuery.trim() });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-dark">
      {/* Header */}
      <header className="border-b border-border obsidian-glass">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </Button>
            <div className="flex items-center gap-3">
              <img 
                src="/lovable-uploads/e601b2a4-d63b-447f-b8f5-3e03625e19d6.png" 
                alt="Ascendry" 
                className="h-6 w-auto"
              />
              <span className="hidden md:block text-2xl font-bold text-glow">
                Ascendry
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Search Section */}
      <section className="py-8 px-4">
        <div className="container mx-auto max-w-4xl">
          <h1 className="text-3xl font-bold mb-6 text-center text-glow">Search Mentors</h1>
          
          {/* Search Form */}
          <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto mb-8">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
            <Input
              type="text"
              placeholder="Search by name, expertise, credits, or specialty..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-16 py-3 text-lg bg-card/50 border-border/50 rounded-full focus:bg-card/80 transition-all duration-200"
            />
            <Button 
              type="submit"
              size="sm"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 rounded-full px-4"
            >
              Search
            </Button>
          </form>

          {/* Results Count */}
          <div className="mb-6 text-center">
            {loading ? (
              <p className="text-muted-foreground">Searching...</p>
            ) : performedQuery ? (
              <p className="text-muted-foreground">
                Found {filteredMentors.length} mentor{filteredMentors.length !== 1 ? 's' : ''} matching "{performedQuery}"
              </p>
            ) : (
              <p className="text-muted-foreground">Enter a search term to find mentors</p>
            )}
          </div>

          {/* Results Grid */}
          {performedQuery && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMentors.map((mentor) => (
                <Card key={mentor.id} className="h-full obsidian-card hover:scale-105 transition-transform duration-300 group">
                  <CardContent className="p-6 flex flex-col h-full">
                    {/* Profile Image */}
                    <div className="flex flex-col items-center mb-6">
                      <div className="relative mb-4">
                        <div className="w-32 h-32 mx-auto rounded-lg overflow-hidden bg-muted">
                          <img
                            src={mentor.profile_image_url || defaultAvatar}
                            alt={`${mentor.first_name} ${mentor.last_name}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = defaultAvatar;
                            }}
                          />
                        </div>
                        <MentorTierBadge tier={mentor.tier} mentorName={`${mentor.first_name} ${mentor.last_name}`} className="absolute -top-2 -right-2" />
                      </div>
                      
                      {/* Name and Basic Info */}
                      <div className="text-center mb-4">
                        <h4 className="text-xl font-bold text-primary mb-1">
                          {mentor.first_name} {mentor.last_name}
                        </h4>
                        <p className="text-sm text-muted-foreground mb-2">{mentor.specialty}</p>
                        <div className="flex items-center justify-center space-x-4 text-sm text-muted-foreground">
                          <div className="flex items-center">
                            <Star className="w-4 h-4 fill-yellow-500 text-yellow-500 mr-1" />
                            <span>{mentor.rating ? mentor.rating.toFixed(1) : 'New'}</span>
                          </div>
                          <span>${(mentor.hourly_rate / 100).toFixed(0)}/hr</span>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-grow space-y-4">
                      {/* Expertise */}
                      {mentor.expertise && mentor.expertise.length > 0 && (
                        <div>
                          <h5 className="text-sm font-semibold text-muted-foreground mb-2">Expertise</h5>
                          <div className="flex flex-wrap gap-1">
                            {mentor.expertise.slice(0, 3).map((skill, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                            {mentor.expertise.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{mentor.expertise.length - 3} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Credits */}
                      {mentor.credits && mentor.credits.length > 0 && (
                        <div>
                          <h5 className="text-sm font-semibold text-muted-foreground mb-2">Credits</h5>
                          <TruncatedCredits 
                            credits={mentor.credits} 
                            mentorId={mentor.id}
                            maxLines={3}
                          />
                        </div>
                      )}
                    </div>

                    {/* Action Button */}
                    <div className="mt-6">
                      <Button 
                        className="w-full energy-button" 
                        asChild
                      >
                        <Link to={`/mentor/${mentor.id}`}>
                          <Calendar className="w-4 h-4 mr-1" />
                          View Profile
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* No Results */}
          {performedQuery && !loading && filteredMentors.length === 0 && (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold mb-2">No mentors found</h3>
              <p className="text-muted-foreground mb-4">
                Try searching with different keywords or browse all mentors.
              </p>
              <Button variant="outline" asChild>
                <Link to="/mentors">Browse All Mentors</Link>
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <ComplianceFooter />
    </div>
  );
};

export default SearchResults;